import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Card, Collapse, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { ReconciliationApi } from '@/api/domains/reconciliation.api';
import { ReconciliationQueryKeys } from '@/api/domains/reconciliation.query-keys';
import type { DocumentDetailsResponse } from '@/api/domains/reconciliation.types';
import { FilterButton, RoundedButton } from '@/components/buttons';
import { SearchField } from '@/components/form-controls';
import { Label } from '@/components/labels';
import { DataGridContainer } from '@/components/ReactDataGrid';
import { Header } from '@/layouts/Header';
import { PageContainer, PageContent } from '@/layouts/Page';
import { formatDateTime, formatNumberByThousands } from '@/shared/helpers/format.helpers';
import { OrdersPaths } from '@/shared/lib/react-router';

import { resetReconciliationFileStore, useReconciliationDocumentType } from '../reconciliation-file.store';
import { ReconciliationResultDataGrid } from './ReconciliationResultDataGrid';

// ----------------------------------------------------------------------

type MetaFieldProps = {
  label: string;
  value: string;
  tooltip?: string;
};

const MetaField = ({ label, value, tooltip }: MetaFieldProps) => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography variant="Body/medium" sx={{ color: '#637381', letterSpacing: '0.25px' }}>
      {label}
    </Typography>
    {tooltip && (
      <Tooltip title={tooltip} arrow>
        <InfoOutlinedIcon sx={{ width: 14, height: 14, color: 'primary.main', cursor: 'pointer' }} />
      </Tooltip>
    )}
    <Typography variant="Body/medium" sx={{ letterSpacing: '0.25px' }}>
      {value}
    </Typography>
  </Stack>
);

// ----------------------------------------------------------------------

type HeaderCardProps = {
  result: DocumentDetailsResponse | undefined;
  onClose: () => void;
};

const ReconciliationHeaderCard = ({ result, onClose }: HeaderCardProps) => {
  const { t } = useTranslation('orders-page');
  const documentType = useReconciliationDocumentType();
  const [isExpanded, setIsExpanded] = useState(true);

  const documentTypeLabel =
    documentType === 'specification'
      ? t('modals.reconciliation-upload.document-types.specification')
      : t('modals.reconciliation-upload.document-types.invoice');

  const itemsCount = result?.items?.length ?? 0;
  const positionsCount = result?.order_positions_count ?? 0;

  const documentTotalPrice = result?.total_price_with_vat ?? 0;

  const orderNumber = result?.order.document_number ?? '';
  const orderDate = result?.order.created_at ? formatDateTime(result.order.created_at, 'dd.MM.yyyy HH:mm:ss') : '';

  return (
    <Card variant="outlined" sx={{ flexShrink: 0 }}>
      <Stack direction="row" alignItems="center" sx={{ px: 2, py: 2 }}>
        <IconButton size="small" onClick={onClose} sx={{ mr: 1 }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>

        <Typography variant="Headline/Bold/small">{t('reconciliation.result.page-title')}</Typography>

        {orderNumber && (
          <Typography variant="Body/large" color="text.secondary" sx={{ ml: 1.5 }}>
            {orderNumber}
          </Typography>
        )}

        {orderDate && (
          <Typography variant="Body/large" color="text.secondary" sx={{ ml: 1 }}>
            {orderDate}
          </Typography>
        )}

        <Label sx={{ ml: 1.5, backgroundColor: '#BDD7FF', color: '#0D41E1' }}>
          {t('reconciliation.result.status.reconciled')}
        </Label>

        <Stack direction="row" sx={{ ml: 'auto' }}>
          <IconButton size="small" onClick={() => setIsExpanded(prev => !prev)}>
            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>
      </Stack>

      <Collapse in={isExpanded}>
        <Divider />

        <Stack direction="row" flexWrap="wrap" columnGap={5} rowGap={1} sx={{ px: 2, py: 1.5 }}>
          <MetaField
            label={t('reconciliation.result.meta.distributor')}
            value={
              result
                ? `${result.order.distributor_name}${result.order.distributor_city ? ` (${result.order.distributor_city})` : ''}`
                : '—'
            }
          />
          <MetaField
            label={t('reconciliation.result.meta.filial')}
            value={
              result?.order.owner_name
                ? [result.order.owner_name, result.order.owner_address].filter(Boolean).join(', ')
                : '—'
            }
          />
          <MetaField
            label={t('reconciliation.result.meta.positions')}
            tooltip={t('reconciliation.result.meta.positions-tooltip')}
            value={result ? `${itemsCount} из ${positionsCount}` : '—'}
          />
          <MetaField
            label={t('reconciliation.result.meta.document-total')}
            value={documentTotalPrice ? `${formatNumberByThousands(documentTotalPrice)} UZS` : '—'}
          />
          <MetaField label={t('reconciliation.result.meta.document-type')} value={documentTypeLabel} />
        </Stack>
      </Collapse>
    </Card>
  );
};

// ----------------------------------------------------------------------

const ReconciliationResultPage = () => {
  const { t } = useTranslation('orders-page');
  const navigate = useNavigate();
  const { orderGuid = '' } = useParams<{ orderGuid: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);

  const hasOrderGuid = Boolean(orderGuid);

  const {
    data: result,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ReconciliationQueryKeys.documentDetails(orderGuid),
    queryFn: () => ReconciliationApi.fetchDocumentDetails(orderGuid),
    enabled: hasOrderGuid,
  });

  const isActuallyLoading = isLoading && isFetching;

  const filteredItems = useMemo(
    () =>
      result?.items?.filter(item => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
          item.drug_name.toLowerCase().includes(query) ||
          item.maker_name.toLowerCase().includes(query) ||
          (item.order_position?.drug_full_name.toLowerCase().includes(query) ?? false)
        );
      }) ?? [],
    [result?.items, searchQuery],
  );

  const handleClose = () => {
    resetReconciliationFileStore();
    navigate(OrdersPaths.ROOT);
  };

  if (!hasOrderGuid) return null;

  return (
    <PageContainer title={t('reconciliation.result.page-title')}>
      <Header hasBreadcrumbs />

      <PageContent spacing={1}>
        <ReconciliationHeaderCard result={result} onClose={handleClose} />

        <Card variant="outlined" sx={{ flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ px: 2, py: '10px' }}>
            <SearchField
              value={searchQuery}
              size="small"
              placeholder={t('main-toolbar.search.placeholder')}
              onChange={e => setSearchQuery(e.target.value)}
              onClear={() => setSearchQuery('')}
            />
            <FilterButton isActive={isFiltersVisible} onClick={() => setIsFiltersVisible(prev => !prev)} />
          </Stack>
        </Card>

        <DataGridContainer>
          <ReconciliationResultDataGrid
            items={filteredItems}
            isLoading={isActuallyLoading}
            isFiltersVisible={isFiltersVisible}
          />
        </DataGridContainer>
      </PageContent>

      <Stack direction="row" justifyContent="flex-end" sx={{ p: 2, borderTop: theme => theme.shape.divider }}>
        <RoundedButton variant="outlined" onClick={handleClose}>
          {t('reconciliation.result.actions.close')}
        </RoundedButton>
      </Stack>
    </PageContainer>
  );
};

export default ReconciliationResultPage;
